import {tryCall} from '../../utils';
import {Ref, useCallback, useMemo} from 'react';
import {isFunction} from 'underscore';
import {List, ListProps} from '../../layout';
import {Renderable, RenderableFunction} from '../../core';
import {Selectors} from '../controls.selectors';
import SelectableListContext, {SelectableListContextState} from './selectable-list-context';
import {CheckboxPlacement} from './types';

type ChildrenT<T> = RenderableFunction<SelectableListContextState<T>> | Renderable;

export type SelectableListProps<T> = {
	value: T[];
	onChange: (value: T[]) => void;
	checkboxPlacement: CheckboxPlacement,
	ref?: Ref<HTMLDivElement>;
	children?: ChildrenT<T>;
} & Omit<ListProps, 'onChange' | 'children'>

function renderChild<T>(context: SelectableListContextState<T>, children: ChildrenT<T>): Renderable {
	if (isFunction(children)) {
		return children(context);
	} else {
		return children;
	}
}

export function SelectableList<T extends any>({value, onChange, children, checkboxPlacement, ...props}: SelectableListProps<T>) {
	const toggleValue = useCallback((toggleValue: T) => {
		const checked = value?.indexOf(toggleValue) > -1;
		if (checked) {
			tryCall(onChange, [...value.filter(v => v !== toggleValue)]);
		} else {
			tryCall(onChange, [...value, toggleValue]);
		}
	}, [value, onChange]);

	const context = useMemo<SelectableListContextState<T>>(() => ({
		value,
		toggleValue: toggleValue,
		checkboxPlacement: checkboxPlacement || 'none',
	}), [value, toggleValue, checkboxPlacement]);

	return <SelectableListContext.Provider value={context}>
		<List data-cy={Selectors.SelectableList} {...props}>{renderChild(context, children)}</List>
	</SelectableListContext.Provider>;
}

export default SelectableList;
